/**
 * Application state (Zustand — small, no ceremony).
 *
 * Units at this layer are the ones the UI shows: km/h and meteorological
 * degrees. Conversion to SI and to the math-convention vector happens exactly
 * once, at the physics boundary in `buildSolveInput`.
 */

import { create } from 'zustand';
import type { FieldSlice, SolveDiagnostics } from '../physics/types';
import type { ContourSet } from '../worker/api';
import type { CrossSection } from '../physics/crossSection';
import type { ProviderId } from '../dem/types';
import { windAtHeight, type Forecast, type ModelId } from '../weather/openMeteo';
import type { RasterMode } from '../render/flowRenderer';
import { metDegreesToMathRad } from '../physics/terrain';
import { bruntFromDial } from '../physics/stability';
import { LOWEST_RESOLVED_AGL } from '../physics/verticalGrid';
import type { SolveInput } from '../physics/types';

/**
 * Height bands above ground, in the steps pilots actually think in. 10 m is
 * the standard met reference height and roughly launch/ground level; 30 m is
 * ridge-soaring height.
 *
 * The lowest band is a physics constraint, not a free choice: the solver sizes
 * its first vertical layer to bracket `LOWEST_RESOLVED_AGL` with cell centres.
 * Adding a lower band means lowering that constant too, or the value shown will
 * be interpolated across a gap where the wind profile is steeply curved.
 */
export const HEIGHT_BANDS = [10, 30, 50, 100, 200, 500] as const;
export type HeightBand = (typeof HEIGHT_BANDS)[number];

if (HEIGHT_BANDS[0] < LOWEST_RESOLVED_AGL) {
  console.warn(
    `Lowest height band (${HEIGHT_BANDS[0]} m) is below what the vertical grid ` +
      `resolves (${LOWEST_RESOLVED_AGL} m); readings there will be interpolated ` +
      `across a gap in the profile.`
  );
}

/**
 * The free-stream wind is always anchored at this height above ground, whatever
 * band is being displayed. Anchoring at the *displayed* band instead would mean
 * every change of display height altered the input wind and forced a re-solve;
 * with a fixed anchor, switching bands is a pure resample of the solved field.
 */
export const REFERENCE_HEIGHT = 100;

export type Quality = 'fast' | 'balanced' | 'detailed' | 'ultra';

/**
 * Working-grid resolution. Cost is linear in cell count — the elliptic solve
 * dominates, and multigrid keeps the iteration count flat with size — so these
 * timings scale predictably. Measured on a ~20 km domain:
 *
 *   fast     128²×18  ~0.4 s   cell ≈ 156 m
 *   balanced 192²×22  ~1.1 s   cell ≈ 104 m
 *   detailed 256²×26  ~2.2 s   cell ≈  78 m
 *   ultra    320²×30  ~3.5 s   cell ≈  63 m
 *
 * Sizes are all even and coarsen to at least 8 cells, which the multigrid
 * hierarchy requires.
 */
export const QUALITY_SETTINGS: Record<
  Quality,
  { gridSize: number; nz: number; tolerance: number; maxCycles: number; label: string }
> = {
  fast: { gridSize: 128, nz: 18, tolerance: 2e-3, maxCycles: 25, label: 'Fast' },
  balanced: { gridSize: 192, nz: 22, tolerance: 1e-3, maxCycles: 30, label: 'Balanced' },
  detailed: { gridSize: 256, nz: 26, tolerance: 5e-4, maxCycles: 40, label: 'Detailed' },
  ultra: { gridSize: 320, nz: 30, tolerance: 5e-4, maxCycles: 40, label: 'Ultra' },
};

export type Status =
  | { kind: 'idle' }
  | { kind: 'loading-dem'; provider?: ProviderId }
  | { kind: 'solving' }
  | { kind: 'ready' }
  | { kind: 'error'; message: string };

export interface LayerState {
  raster: RasterMode;
  rasterOpacity: number;
  particles: boolean;
  particleOpacity: number;
  /** turbulent jitter applied to streamlines, 0 = mean flow only */
  turbulence: number;
  separation: boolean;
  liftBand: boolean;
  hillshade: boolean;
  /**
   * Altitude the watermark layer floods to, metres above sea level. Re-anchored
   * into the loaded terrain's own range whenever new ground is loaded — a fixed
   * default is either underground or in the stratosphere depending on where you
   * open the map.
   */
  watermarkAltitude: number;
  /** Fold the current wind into the hike-and-fly score. */
  hikeFlyWind: boolean;
}

export interface SavedSite {
  id: string;
  name: string;
  lat: number;
  lon: number;
  zoom: number;
}

export interface AppState {
  // --- location ---
  lat: number;
  lon: number;
  zoom: number;
  /** the span the DEM was loaded for, metres; drives the "reload terrain" prompt */
  loadedSpan: number | null;
  loadedCentre: [number, number] | null;

  // --- wind ---
  mode: 'manual' | 'live';
  directionDeg: number;
  speedKmh: number;
  stability: number;
  /** true while the stability dial is following the forecast */
  stabilityAuto: boolean;
  heightBand: HeightBand;

  // --- live ---
  forecast: Forecast | null;
  forecastHour: number;
  model: ModelId;
  forecastError: string | null;

  // --- settings ---
  quality: Quality;
  demProvider: ProviderId | 'auto';
  empirical: boolean;
  liftThreshold: number;
  layers: LayerState;

  // --- results ---
  status: Status;
  slice: FieldSlice | null;
  diagnostics: SolveDiagnostics | null;
  contours: ContourSet | null;
  attribution: string | null;
  activeProvider: ProviderId | null;
  /** Elevation range of the loaded grid, metres — bounds for the altitude slider. */
  terrainRange: [number, number] | null;
  /** True once the glide field has been computed for the loaded terrain. */
  hikeFlyReady: boolean;

  // --- cross-section ---
  crossSectionMode: boolean;
  crossSectionLine: [[number, number], [number, number]] | null;
  crossSection: CrossSection | null;

  // --- sites & chrome ---
  sites: SavedSite[];
  disclaimerAccepted: boolean;
  sidebarOpen: boolean;

  set: (partial: Partial<AppState>) => void;
  setLayers: (partial: Partial<LayerState>) => void;
}

export const useStore = create<AppState>((set) => ({
  // Tiger Mountain, Issaquah — the home site this gets checked against most.
  lat: 47.5005,
  lon: -122.0216,
  zoom: 12.4,
  loadedSpan: null,
  loadedCentre: null,

  mode: 'manual',
  directionDeg: 315,
  speedKmh: 25,
  stability: 0,
  stabilityAuto: true,
  heightBand: 100,

  forecast: null,
  forecastHour: 0,
  model: 'best_match',
  forecastError: null,

  // Phones get the cheaper grid by default. The solve is single-threaded in a
  // Worker and 192²×22 takes several seconds on a handset; 'fast' keeps it
  // responsive, and the quality picker is there if you want more detail.
  quality:
    typeof window === 'undefined' || window.innerWidth > 820 ? 'balanced' : 'fast',
  demProvider: 'auto',
  empirical: true,
  liftThreshold: 1.2,
  layers: {
    // Air character on open: it is the layer that answers "where should I not
    // be today" in one glance, which is what someone opening the app wants
    // before they start comparing scales.
    raster: 'hazard',
    rasterOpacity: 0.5,
    particles: true,
    particleOpacity: 0.9,
    turbulence: 0.85,
    separation: true,
    liftBand: true,
    hillshade: true,
    watermarkAltitude: 1000,
    hikeFlyWind: false,
  },

  status: { kind: 'idle' },
  slice: null,
  diagnostics: null,
  contours: null,
  attribution: null,
  activeProvider: null,
  terrainRange: null,
  hikeFlyReady: false,

  crossSectionMode: false,
  crossSectionLine: null,
  crossSection: null,

  sites: [],
  disclaimerAccepted: false,
  // On a phone the sheet covers most of the map, so it starts collapsed —
  // the map is what you want to see first when you open this at launch.
  sidebarOpen: typeof window === 'undefined' || window.innerWidth > 820,

  set: (partial) => set(partial),
  setLayers: (partial) => set((s) => ({ layers: { ...s.layers, ...partial } })),
}));

if (import.meta.env.DEV && typeof window !== 'undefined') {
  // Handle for inspecting solved fields from the console during development;
  // never exposed in a production build.
  (window as unknown as { __store?: typeof useStore }).__store = useStore;
}

/**
 * Where the watermark line sits when terrain is loaded, or when the previous
 * altitude no longer means anything for the ground now under the map.
 *
 * Low in the relief rather than halfway up it: the layer is there to show the
 * air below the ridges, and starting at the midpoint floods the valleys so
 * completely that the shape of them is lost.
 */
export function defaultWatermarkAltitude(minH: number, maxH: number): number {
  const alt = minH + 0.35 * (maxH - minH);
  return Math.round(alt / 25) * 25;
}

/** Free-stream wind for the current mode, in SI. */
export function currentWind(s: AppState): { speedMs: number; directionDeg: number } {
  if (s.mode === 'live' && s.forecast) {
    const hour = s.forecast.hours[s.forecastHour];
    if (hour) {
      const w = windAtHeight(hour, REFERENCE_HEIGHT);
      if (Number.isFinite(w.speed)) {
        return { speedMs: w.speed, directionDeg: w.directionDeg };
      }
    }
  }
  return { speedMs: s.speedKmh / 3.6, directionDeg: s.directionDeg };
}

/**
 * The single conversion point from UI units to physics units.
 *
 * Meteorological degrees (wind comes *from*) become a math-convention angle
 * (where the air is going, CCW from east); km/h becomes m/s. Nothing below this
 * function should ever see the UI's conventions.
 */
export function buildSolveInput(
  s: AppState,
  grid: { data: Float32Array; width: number; height: number; cellSize: number }
): SolveInput {
  const q = QUALITY_SETTINGS[s.quality];
  const { speedMs, directionDeg } = currentWind(s);
  const hour = s.mode === 'live' && s.forecast ? s.forecast.hours[s.forecastHour] : null;

  const blHeight = hour?.blHeight ?? 1200;
  const brunt =
    hour && s.stabilityAuto ? hour.stability.brunt : bruntFromDial(s.stability);

  return {
    terrain: grid,
    speed: speedMs,
    directionRad: metDegreesToMathRad(directionDeg),
    referenceHeight: REFERENCE_HEIGHT,
    stability: s.stability,
    z0Default: 0.1,
    brunt,
    blHeight,
    nz: q.nz,
    empirical: s.empirical,
    tolerance: q.tolerance,
    maxCycles: q.maxCycles,
  };
}
